import i18n from 'i18n';
import React, {useEffect, useState} from 'react';
import {EventActorsList} from './EventActorsList';
import {Tooltip} from '@/components/tooltips';
import {EVENT_TYPE_CALL, EVENT_TYPE_CHAT, EVENT_TYPE_EMAIL, EVENT_TYPE_FEEDBACK,} from 'utils/eventConstants';
import Icon from '@/components/icons/Icon';
import RenderNoDataAvailable, {RestrictedFieldIndicator} from 'components/utils/DataNotAvailable';
import {buildAgentDashboardURL, buildCustomerDashboardURL} from '@/utils/urlBuilder';
import { isModuleEnabled } from '@/utils/modules';
import {isFieldRestricted} from '@/utils/fieldRestrictions';

interface EventAgent {
    id?: number | null;
    agentName: string;
    value?: string | null;
    type?: string;
}

interface EventCustomer {
    id?: number | string | null;
    customerName: string;
    value?: string | null;
    optIn?: boolean | null;
    optInDate?: string | null;
}

interface UserData {
    indexAnonymized?: boolean;
    currentModules?: {
        'addon-coop'?: boolean;
    };
    permissions?: string[];
}

interface Event {
    eventActors?: any[];
    eventType?: {
        name: string;
    };
}

interface SearchEvent {
    event_actors?: any[];
    event_type_name?: string;
    agent_first_name?: string;
    agent_last_name?: string;
    agent_id?: number;
    customer_first_name?: string;
    customer_last_name?: string;
    customer_external_id?: string;
    customer_id?: number;
}

interface EventAgentCustomerNameBlockProps {
    eventDetails: Event | null;
    eventIndexDetails: SearchEvent | null;
    userData: UserData | null;
    restrictedFields?: string[] | null;
}

const EventAgentCustomerNameBlock: React.FC<EventAgentCustomerNameBlockProps> = ({eventDetails, eventIndexDetails, userData, restrictedFields}) => {
    const [eventAgent, setEventAgent] = useState<EventAgent | EventAgent[] | string | null>(null);
    const [eventCustomer, setEventCustomer] = useState<EventCustomer | null>(null);

    useEffect(() => {
        if (eventDetails && eventIndexDetails && userData) {
            const {eventAgent, eventCustomer} = EventActorsList(
                eventDetails,
                eventIndexDetails,
                userData,
                restrictedFields
            );
            setEventAgent(eventAgent);
            setEventCustomer(eventCustomer);
        }
    }, [eventDetails, eventIndexDetails, userData, restrictedFields]);

    if (!eventAgent || !eventCustomer) {
        return <>
            <tr>
                <td>{i18n.t('jt_loading_data_general.text')}</td>
            </tr>
        </>;
    }

    const agentRestricted = isFieldRestricted('agent_first_name', restrictedFields);
    const customerRestricted = isFieldRestricted('customer_first_name', restrictedFields);

    if (eventDetails?.eventType?.name === EVENT_TYPE_CALL) {
        return (
            <>
                <tr>
                    <td>
                        <h5 className="d-block">{i18n.t('jt_agent.text')}</h5>
                        <span className="d-block" style={{fontSize: '0.9rem'}}>
                            {agentRestricted
                                ? <RestrictedFieldIndicator />
                                : eventAgent && !userData?.indexAnonymized && (eventAgent as EventAgent).id ?
                                <a
                                    href={buildAgentDashboardURL((eventAgent as EventAgent).id!)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {(eventAgent as EventAgent).agentName}
                                </a>
                                : RenderNoDataAvailable()}
                        </span>
                    </td>
                    <td>
                        <h5 className="d-block">{i18n.t('jt_customer.text')}</h5>
                        <span className="d-block" style={{fontSize: '0.9rem'}}>
                            {customerRestricted
                                ? <RestrictedFieldIndicator />
                                : eventCustomer && !userData?.indexAnonymized && eventCustomer.id ? (
                                <>
                                    <a
                                        href={buildCustomerDashboardURL(eventCustomer.id)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {eventCustomer.customerName}
                                    </a>
                                    <br />

                                    {isModuleEnabled(userData, 'addon-coop') ?
                                        <span> {i18n.t('jt_customer_opt_in.text')}
                                            {eventCustomer.optIn ? (
                                                <>
                                                    <Icon name="mdi-check" className="text-success mx-1" />
                                                    {eventCustomer.optInDate ? `(${eventCustomer.optInDate})` : null}

                                                </>
                                            ) : (
                                                <Icon name="mdi-close" className="text-danger mx-1" />
                                            )}
                                        </span>
                                        : <span> {i18n.t('jt_customer_opt_in.text')}
                                            <Tooltip
                                                content={i18n.t('tt_the_following.text')}
                                                placement="top"
                                            >
                                                <div style={{
                                                    display: 'inline-block',
                                                }}
                                                >
                                                    &nbsp;<Icon name="mdi-alert-circle" className="text-danger" size='1em' />
                                                </div>
                                            </Tooltip>
                                        </span>
                                    }


                                </>
                            ) : RenderNoDataAvailable()}
                        </span>
                    </td>
                </tr>
            </>
        );
    } else if (eventDetails?.eventType?.name === EVENT_TYPE_CHAT) {
        return (<>
            <tr>
                <td>
                    <h5 className="d-block">{i18n.t('jt_agent.text')}</h5>
                    <span className="d-block" style={{fontSize: '0.9rem'}}>
                        {agentRestricted
                            ? <RestrictedFieldIndicator />
                            : eventAgent && !userData?.indexAnonymized ? (
                            (eventAgent as EventAgent).agentName === (eventAgent as EventAgent).value ? (
                                <span>{(eventAgent as EventAgent).value}</span>
                            ) : (
                                <span>
                                    {(eventAgent as EventAgent).agentName}
                                    <br />
                                    {(eventAgent as EventAgent).value}
                                </span>
                            )
                        ) : (
                            RenderNoDataAvailable()
                        )}
                    </span>
                </td>

                <td>
                    <h5 className="d-block">{i18n.t('jt_customer.text')}</h5>
                    <span className="d-block" style={{fontSize: '0.9rem'}}>
                        {customerRestricted
                            ? <RestrictedFieldIndicator />
                            : eventCustomer && !userData?.indexAnonymized ? (
                            eventCustomer.customerName === eventCustomer.value ? (
                                <span>{eventCustomer.value}</span>
                            ) : (
                                <span>
                                    {eventCustomer.customerName}
                                    <br />
                                    {eventCustomer.value}
                                </span>
                            )
                        ) : (
                            RenderNoDataAvailable()
                        )}
                    </span>
                </td>
            </tr>
        </>
        );

    } else if (eventDetails?.eventType?.name === EVENT_TYPE_EMAIL) {
        return (
            <>
                <tr>
                    <td>
                        <h5 className="d-block">{i18n.t('jt_sender.text')}</h5>
                        <span className="d-block" style={{fontSize: '0.9rem'}}>
                            {customerRestricted
                                ? <RestrictedFieldIndicator />
                                : eventCustomer && !userData?.indexAnonymized ? (
                                eventCustomer.customerName === eventCustomer.value ? (
                                    <span>{eventCustomer.value}</span>
                                ) : (
                                    <span>
                                        {eventCustomer.customerName}
                                        <br />
                                        {eventCustomer.value}
                                    </span>
                                )
                            ) : (
                                RenderNoDataAvailable()
                            )}
                        </span>
                    </td>

                    <td>
                        <h5 className="d-block">{(eventAgent as EventAgent[]).length > 1 ? i18n.t('jt_recipients.text') : i18n.t('jt_recipient.text')}</h5>
                        <span className="d-block" style={{fontSize: '0.9rem'}}>
                            {agentRestricted
                                ? <RestrictedFieldIndicator />
                                : eventAgent && !userData?.indexAnonymized ? (
                                Array.isArray(eventAgent) ? (
                                    (eventAgent as EventAgent[]).map((agent, index) => (
                                        <div key={index}>
                                            <span className="mr-1">{agent.type}:</span>
                                            {agent.agentName === agent.value ? (
                                                <span>{agent.value}</span>
                                            ) : (
                                                <span>
                                                    {agent.agentName}
                                                    <br />
                                                    {agent.value}
                                                </span>
                                            )}
                                            {index < (eventAgent as EventAgent[]).length - 1 && <br />}
                                        </div>
                                    ))
                                ) : (
                                    <div>
                                        <span className="mr-1">{(eventAgent as EventAgent).type}:</span>
                                        {(eventAgent as EventAgent).agentName === (eventAgent as EventAgent).value ? (
                                            <span>{(eventAgent as EventAgent).value}</span>
                                        ) : (
                                            <span>
                                                {(eventAgent as EventAgent).agentName}
                                                <br />
                                                {(eventAgent as EventAgent).value}
                                            </span>
                                        )}
                                    </div>
                                )) : (
                                RenderNoDataAvailable()
                            )}
                        </span>
                    </td>
                </tr>
            </>
        );

    } else if (eventDetails?.eventType?.name === EVENT_TYPE_FEEDBACK) {
        return (<>
            <tr>
                <td colSpan={2}>
                    <h5 className="d-block">{i18n.t('jt_responder.text')}</h5>
                    <span className="d-block" style={{fontSize: '0.9rem'}}>
                        {customerRestricted
                            ? <RestrictedFieldIndicator />
                            : eventCustomer && !userData?.indexAnonymized ?
                            (eventCustomer.customerName === eventCustomer.value ? (
                                <>{eventCustomer.value}</>) : (
                                    <>
                                    {eventCustomer.customerName}
                                    <br />
                                    {eventCustomer.value}
                                </>)
                            ) : (RenderNoDataAvailable())}
                    </span>
                </td>
            </tr>
        </>);
    } else {
        return (
            <tr>
                <td colSpan={2}>{i18n.t('jt_no_data_available.text')}</td>
            </tr>
        );
    }
}

export default EventAgentCustomerNameBlock;


